import type {FirebaseApp} from "firebase/app";
import type {Messaging} from "firebase/messaging";

/**
 * Firebase Cloud Messaging (web push) config — AC-649.
 *
 * Web push is delivered by FCM. The player's browser obtains an FCM registration
 * token (getToken) and we persist it on the backend (POST /api/v1/push-tokens,
 * platform=web); the backend/Customer.io then sends pushes to that token. All of
 * these values are non-secret public Firebase identifiers and are safe in the
 * client bundle. The FCM *server* key / service account stays server-side only.
 *
 * The real values come from the Firebase project (provisioned by DevOps). When
 * any required value (project id / VAPID key) is missing the integration no-ops,
 * so a missing env var never crashes the build or the app.
 */
export const firebaseConfig = {
    apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
    authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
    storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
} as const;

/** Public VAPID key pair (Web Push certificate) from the Firebase console. */
export const FIREBASE_VAPID_KEY = process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY;

/** FCM is only wired up when the project id and VAPID key are configured. */
export const isFcmConfigured = (): boolean =>
    !!firebaseConfig.projectId && !!FIREBASE_VAPID_KEY;

/**
 * URL for registering the FCM service worker. `public/` files get no build-time
 * env substitution and a service worker cannot read `process.env`, so we pass
 * the (public) Firebase config to `firebase-messaging-sw.js` as query params;
 * the worker parses them from `self.location.search`. This keeps the env vars
 * the single source of truth for the config.
 */
export const getFcmServiceWorkerUrl = (): string => {
    const params = new URLSearchParams();
    if (firebaseConfig.apiKey) params.set("apiKey", firebaseConfig.apiKey);
    if (firebaseConfig.authDomain) params.set("authDomain", firebaseConfig.authDomain);
    if (firebaseConfig.projectId) params.set("projectId", firebaseConfig.projectId);
    if (firebaseConfig.messagingSenderId)
        params.set("messagingSenderId", firebaseConfig.messagingSenderId);
    if (firebaseConfig.appId) params.set("appId", firebaseConfig.appId);
    return `/firebase-messaging-sw.js?${params.toString()}`;
};

/**
 * Lazily initialize (or reuse) the Firebase app singleton. `firebase/app` is
 * `import()`-ed here rather than statically — this module is reachable from
 * `baseQueryWithRefresh` (every RTK Query endpoint) via localToken.ts, so a
 * static SDK import here would ship it in the shell for every visitor (AC-803).
 */
export const getFirebaseApp = async (): Promise<FirebaseApp> => {
    const {getApps, initializeApp} = await import("firebase/app");
    return getApps()[0] ?? initializeApp(firebaseConfig);
};

/**
 * Returns a Messaging instance, or null when FCM is not configured / not
 * supported in this browser (Safari private mode, no service worker, etc.).
 * Never throws.
 */
export const getMessagingIfSupported = async (): Promise<Messaging | null> => {
    if (typeof window === "undefined" || !isFcmConfigured()) return null;
    try {
        const {getMessaging, isSupported} = await import("firebase/messaging");
        if (!(await isSupported())) return null;
        return getMessaging(await getFirebaseApp());
    } catch (err) {
        console.error("[FCM] messaging unavailable", err);
        return null;
    }
};
